import {
  Box,
  Heading,
  HStack,
  Icon,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Tooltip,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FiFileText, FiInbox } from 'react-icons/fi'
import {
  useSupportFormTemplate,
  useSupportReceives,
  useUpdateSupportFormTemplate,
  useUpdateSupportReceive,
} from '@/hooks/useAdminData'
import type {
  SupportFormAttachmentPolicy,
  SupportFormCategory,
  SupportFormSubcategory,
  SupportFormSubtopic,
  SupportFormTemplate,
  SupportFormTemplateSchema,
  SupportReceive,
} from '@/types'
import { useToast } from '@/hooks/useToast'
import { useNetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import { withNetworkStorageKey } from '@/utils/storageKeys'
import { useAdminTabQuery } from '@/navigation/adminTabQuery'
import { SupportReceiveModal } from './SupportReceiveModal'
import { SupportReceivesTab } from './SupportReceivesTab'
import { SupportTemplateTab } from './SupportTemplateTab'
import {
  SUPPORT_STATUSES,
  DEFAULT_TEMPLATE_DEFAULTS,
  type TemplateDefaultsState,
  buildAllowedMimeTypes,
  bytesToMbString,
  createEmptyCategory,
  createEmptySubcategory,
  createEmptySubtopic,
  dedupe,
  ensureArray,
  normalizeCategory,
  normalizeSubtopicType,
  parseJson,
  parsePositiveInt,
  parsePositiveNumber,
} from './supportPageHelpers'

const SUPPORT_TABS = ['template', 'receives'] as const

export function SupportPage() {
  const { t } = useTranslation()
  const toast = useToast()
  const { currentEnv } = useNetworkEnvironment()
  const TAB_STORAGE_KEY = useMemo(
    () => withNetworkStorageKey('admin_portal_support_tab', currentEnv),
    [currentEnv]
  )
  const { activeTabIndex: tabIndex, handleTabChange } = useAdminTabQuery({
    tabs: SUPPORT_TABS,
    defaultTab: 'template',
    storageKey: TAB_STORAGE_KEY,
  })

  const {
    data: templateData,
    isLoading: isTemplateLoading,
    error: templateError,
    refetch: refetchTemplate,
    isFetching: isFetchingTemplate,
  } = useSupportFormTemplate({ enabled: tabIndex === 0 })
  const updateTemplate = useUpdateSupportFormTemplate()

  const [templateSlug, setTemplateSlug] = useState('')
  const [templateName, setTemplateName] = useState('')
  const [templateVersion, setTemplateVersion] = useState('')
  const [templateDefaults, setTemplateDefaults] =
    useState<TemplateDefaultsState>(DEFAULT_TEMPLATE_DEFAULTS)
  const [templateCategories, setTemplateCategories] = useState<
    SupportFormCategory[]
  >([])
  const [templateDirty, setTemplateDirty] = useState(false)

  const template = templateData ?? null
  const applyTemplate = (nextTemplate: SupportFormTemplate | null) => {
    if (!nextTemplate) {
      return
    }

    setTemplateSlug(nextTemplate.slug ?? '')
    setTemplateName(nextTemplate.name ?? '')
    setTemplateVersion(String(nextTemplate.version ?? ''))
    const defaults = nextTemplate.schema?.defaults
    setTemplateDefaults({
      maxMessageLength: String(defaults?.maxMessageLength ?? 500),
      maxAttachmentSizeMb: bytesToMbString(
        defaults?.maxAttachmentSizeBytes ?? 5 * 1024 * 1024
      ),
      maxAttachments: String(defaults?.maxAttachments ?? 3),
    })
    setTemplateCategories(
      ensureArray(nextTemplate.schema?.categories).map(normalizeCategory)
    )
    setTemplateDirty(false)
  }

  useEffect(() => {
    if (!template || templateDirty) return
    applyTemplate(template)
  }, [template?.id, template?.version, template?.updatedAt, templateDirty])

  const handleTemplateRefresh = async () => {
    const result = await refetchTemplate()
    if (result.error) {
      toast.error({
        title: t('support.template.refreshFailed'),
      })
      return
    }

    if (result.data) {
      applyTemplate(result.data)
    } else {
      setTemplateDirty(false)
    }
    toast.success({
      title: t('support.template.refreshed'),
    })
  }

  const markTemplateDirty = () => setTemplateDirty(true)

  const updateCategory = (
    index: number,
    updater: (category: SupportFormCategory) => SupportFormCategory
  ) => {
    setTemplateCategories(prev =>
      prev.map((category, idx) =>
        idx === index ? updater(category) : category
      )
    )
    markTemplateDirty()
  }

  const updateSubcategory = (
    categoryIndex: number,
    subIndex: number,
    updater: (subcategory: SupportFormSubcategory) => SupportFormSubcategory
  ) => {
    updateCategory(categoryIndex, category => ({
      ...category,
      subcategories: category.subcategories.map((subcategory, idx) =>
        idx === subIndex ? updater(subcategory) : subcategory
      ),
    }))
  }

  const updateSubtopic = (
    categoryIndex: number,
    subIndex: number,
    topicIndex: number,
    updater: (subtopic: SupportFormSubtopic) => SupportFormSubtopic
  ) => {
    updateSubcategory(categoryIndex, subIndex, subcategory => ({
      ...subcategory,
      subtopics: ensureArray(subcategory.subtopics).map((subtopic, idx) =>
        idx === topicIndex ? updater(subtopic) : subtopic
      ),
    }))
  }

  const addCategory = () => {
    setTemplateCategories(prev => [...prev, createEmptyCategory()])
    markTemplateDirty()
    toast.info({
      title: t('support.template.categoryAdded'),
    })
  }

  const removeCategory = (index: number) => {
    setTemplateCategories(prev => prev.filter((_, idx) => idx !== index))
    markTemplateDirty()
    toast.info({
      title: t('support.template.categoryRemoved'),
    })
  }

  const addSubcategory = (categoryIndex: number) => {
    updateCategory(categoryIndex, category => ({
      ...category,
      subcategories: [...category.subcategories, createEmptySubcategory()],
    }))
    toast.info({
      title: t('support.template.subcategoryAdded'),
    })
  }

  const removeSubcategory = (categoryIndex: number, subIndex: number) => {
    updateCategory(categoryIndex, category => ({
      ...category,
      subcategories: category.subcategories.filter(
        (_, idx) => idx !== subIndex
      ),
    }))
    toast.info({
      title: t('support.template.subcategoryRemoved'),
    })
  }

  const addSubtopic = (categoryIndex: number, subIndex: number) => {
    updateSubcategory(categoryIndex, subIndex, subcategory => ({
      ...subcategory,
      subtopics: [...ensureArray(subcategory.subtopics), createEmptySubtopic()],
      attachmentPolicy: undefined,
    }))
    toast.info({
      title: t('support.template.subtopicAdded'),
    })
  }

  const removeSubtopic = (
    categoryIndex: number,
    subIndex: number,
    topicIndex: number
  ) => {
    updateSubcategory(categoryIndex, subIndex, subcategory => ({
      ...subcategory,
      subtopics: ensureArray(subcategory.subtopics).filter(
        (_, idx) => idx !== topicIndex
      ),
    }))
    toast.info({
      title: t('support.template.subtopicRemoved'),
    })
  }

  const buildAttachmentPolicy = (
    policy?: SupportFormAttachmentPolicy
  ): SupportFormAttachmentPolicy | undefined => {
    if (!policy?.enabled) {
      return undefined
    }
    const allowedExtensions = dedupe(ensureArray(policy.allowedExtensions))
    const allowedMimeTypes = buildAllowedMimeTypes(
      allowedExtensions,
      ensureArray(policy.allowedMimeTypes)
    )
    const maxFiles = policy.maxFiles
      ? parsePositiveInt(String(policy.maxFiles))
      : null
    const nextPolicy: SupportFormAttachmentPolicy = {
      enabled: true,
      allowedExtensions,
      allowedMimeTypes,
    }
    if (maxFiles) {
      nextPolicy.maxFiles = maxFiles
    }
    return nextPolicy
  }

  const validateAttachmentPolicy = (
    policy: SupportFormAttachmentPolicy | undefined
  ): string | null => {
    if (!policy?.enabled) {
      return null
    }
    if (!policy.allowedExtensions || policy.allowedExtensions.length === 0) {
      return t('support.template.validation.attachmentsTypesRequired')
    }
    if (policy.maxFiles !== undefined && policy.maxFiles <= 0) {
      return t('support.template.validation.attachmentsMaxFilesInvalid')
    }
    return null
  }

  const validateTemplate = (): string | null => {
    if (templateCategories.length === 0) {
      return t('support.template.validation.categoriesRequired')
    }

    const categoryIds = new Set<string>()

    for (const category of templateCategories) {
      const categoryId = category.id.trim()
      const categoryLabel = category.label.trim()

      if (!categoryId) {
        return t('support.template.validation.categoryIdRequired')
      }
      if (!categoryLabel) {
        return t('support.template.validation.categoryLabelRequired')
      }
      if (categoryIds.has(categoryId)) {
        return t('support.template.validation.categoryDuplicate')
      }
      categoryIds.add(categoryId)

      if (!category.subcategories || category.subcategories.length === 0) {
        return t('support.template.validation.subcategoryRequired')
      }

      const subcategoryIds = new Set<string>()

      for (const subcategory of category.subcategories) {
        const subcategoryId = subcategory.id.trim()
        const subcategoryLabel = subcategory.label.trim()

        if (!subcategoryId) {
          return t('support.template.validation.subcategoryIdRequired')
        }
        if (!subcategoryLabel) {
          return t('support.template.validation.subcategoryLabelRequired')
        }
        if (subcategoryIds.has(subcategoryId)) {
          return t('support.template.validation.subcategoryDuplicate')
        }
        subcategoryIds.add(subcategoryId)

        const subtopics = ensureArray(subcategory.subtopics)
        const subtopicType = normalizeSubtopicType(
          subcategory.subtopicType,
          subtopics.length
        )

        if (!subtopicType) {
          const policyError = validateAttachmentPolicy(
            subcategory.attachmentPolicy
          )
          if (policyError) {
            return policyError
          }
        }

        if (subtopicType === 'open') {
          if (subtopics.length !== 1) {
            return t('support.template.validation.subtopicOpenCount')
          }
        }

        if (subtopicType === 'options') {
          if (subtopics.length === 0) {
            return t('support.template.validation.subtopicRequired')
          }
        }

        if (subtopicType) {
          const subtopicIds = new Set<string>()
          for (const subtopic of subtopics) {
            const subtopicId = subtopic.id.trim()
            const subtopicLabel = subtopic.label.trim()

            if (!subtopicId) {
              return t('support.template.validation.subtopicIdRequired')
            }
            if (!subtopicLabel) {
              return t('support.template.validation.subtopicLabelRequired')
            }
            if (subtopicIds.has(subtopicId)) {
              return t('support.template.validation.subtopicDuplicate')
            }
            subtopicIds.add(subtopicId)

            const subtopicPolicyError = validateAttachmentPolicy(
              subtopic.attachmentPolicy
            )
            if (subtopicPolicyError) {
              return subtopicPolicyError
            }
          }
        }
      }
    }

    return null
  }

  const handleTemplateSave = async () => {
    if (!templateSlug.trim() || !templateName.trim()) {
      toast.error({
        title: t('support.template.saveFailed'),
        description: t('support.template.missingFields'),
      })
      return
    }

    if (templateVersion && !Number.isFinite(Number(templateVersion))) {
      toast.error({
        title: t('support.template.saveFailed'),
        description: t('support.template.invalidVersion'),
      })
      return
    }

    const maxMessageLength = parsePositiveInt(templateDefaults.maxMessageLength)
    const maxAttachmentSizeMb = parsePositiveNumber(
      templateDefaults.maxAttachmentSizeMb
    )
    const maxAttachments = parsePositiveInt(templateDefaults.maxAttachments)

    if (!maxMessageLength || !maxAttachmentSizeMb || !maxAttachments) {
      toast.error({
        title: t('support.template.saveFailed'),
        description: t('support.template.validation.defaultsInvalid'),
      })
      return
    }

    const templateErrorMessage = validateTemplate()
    if (templateErrorMessage) {
      toast.error({
        title: t('support.template.saveFailed'),
        description: templateErrorMessage,
      })
      return
    }

    const schema: SupportFormTemplateSchema = {
      defaults: {
        maxMessageLength,
        maxAttachmentSizeBytes: Math.round(maxAttachmentSizeMb * 1024 * 1024),
        maxAttachments,
      },
      categories: templateCategories.map(category => ({
        id: category.id.trim(),
        label: category.label.trim(),
        subcategories: category.subcategories.map(subcategory => {
          const subtopics = ensureArray(subcategory.subtopics).map(
            subtopic => ({
              id: subtopic.id.trim(),
              label: subtopic.label.trim(),
              ...(buildAttachmentPolicy(subtopic.attachmentPolicy)
                ? {
                    attachmentPolicy: buildAttachmentPolicy(
                      subtopic.attachmentPolicy
                    ),
                  }
                : {}),
            })
          )
          const subtopicType = normalizeSubtopicType(
            subcategory.subtopicType,
            subtopics.length
          )
          const subtopicTitle = subcategory.subtopicTitle?.trim() ?? ''
          const subcategoryPolicy = subtopicType
            ? undefined
            : buildAttachmentPolicy(subcategory.attachmentPolicy)
          return {
            id: subcategory.id.trim(),
            label: subcategory.label.trim(),
            subtopics,
            ...(subtopicType ? { subtopicType } : {}),
            ...(subtopicTitle ? { subtopicTitle } : {}),
            ...(subcategoryPolicy
              ? { attachmentPolicy: subcategoryPolicy }
              : {}),
          }
        }),
      })),
    }

    const payload = {
      slug: templateSlug.trim(),
      name: templateName.trim(),
      version: templateVersion ? Number(templateVersion) : undefined,
      schema,
    }

    try {
      const nextTemplate = await updateTemplate.mutateAsync(payload)
      toast.success({
        title: t('support.template.saved'),
      })
      if (nextTemplate) {
        applyTemplate(nextTemplate)
      } else {
        setTemplateDirty(false)
      }
    } catch (error) {
      toast.error({
        title: t('support.template.saveFailed'),
      })
    }
  }

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [filters, setFilters] = useState<Record<string, string>>({ source: '' })
  const receivesParams = useMemo(
    () => ({
      page,
      limit: pageSize,
      status: filters.status || undefined,
      source: filters.source || undefined,
      eventType: filters.eventType || undefined,
      ticketId: filters.ticketId || undefined,
      sourceId: filters.sourceId || undefined,
    }),
    [page, pageSize, filters]
  )

  const {
    data: receivesData,
    isLoading: isReceivesLoading,
    error: receivesError,
    refetch: refetchReceives,
    isFetching: isFetchingReceives,
  } = useSupportReceives(receivesParams, { enabled: tabIndex === 1 })

  const updateReceive = useUpdateSupportReceive()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [selectedReceive, setSelectedReceive] = useState<SupportReceive | null>(
    null
  )
  const [editStatus, setEditStatus] = useState('received')
  const [editNotes, setEditNotes] = useState('')
  const [payloadPreview, setPayloadPreview] = useState('')

  const receives = receivesData?.data ?? []
  const pagination = receivesData?.pagination

  useEffect(() => {
    if (!selectedReceive) return
    setEditStatus(selectedReceive.ticketStatus ?? 'received')
    setEditNotes(selectedReceive.supportNotes ?? '')
    const parsed = parseJson(selectedReceive.payloadJson ?? null)
    setPayloadPreview(parsed ? JSON.stringify(parsed, null, 2) : '')
  }, [selectedReceive])

  const handleEditReceive = (row: SupportReceive) => {
    setSelectedReceive(row)
    onOpen()
  }

  const handleModalClose = () => {
    onClose()
    setSelectedReceive(null)
  }

  const handleRefreshSelectedReceive = async () => {
    const selectedId = selectedReceive?.id
    const result = await refetchReceives()
    if (!selectedId) {
      return
    }
    const refreshedReceive = result.data?.data?.find(
      receive => receive.id === selectedId
    )
    if (refreshedReceive) {
      setSelectedReceive(refreshedReceive)
    }
  }

  const handleReceiveSave = async () => {
    if (!selectedReceive) return
    const payload = {
      ticketStatus: editStatus,
      supportNotes: editNotes.trim() ? editNotes.trim() : null,
    }

    try {
      await updateReceive.mutateAsync({ id: selectedReceive.id, data: payload })
      toast.success({
        title: t('support.receives.updated'),
      })
      handleModalClose()
      await refetchReceives()
    } catch (error) {
      toast.error({
        title: t('support.receives.updateFailed'),
      })
    }
  }

  const cardBg = useColorModeValue('white', 'gray.800')
  const mutedBg = useColorModeValue('gray.50', 'gray.700')
  const subtopicBg = useColorModeValue('gray.100', 'gray.600')
  const subtleBorder = useColorModeValue('gray.200', 'gray.600')

  return (
    <Box>
      <HStack justify="space-between" mb={6} spacing={4} align="flex-start">
        <Box>
          <Heading size="lg">{t('support.title')}</Heading>
          <Text color="gray.500" mt={2}>
            {t('support.subtitle')}
          </Text>
        </Box>
      </HStack>

      <Tabs
        isLazy
        lazyBehavior="keepMounted"
        index={tabIndex}
        onChange={handleTabChange}
      >
        <TabList overflowX={{ base: 'auto', md: 'visible' }} overflowY="hidden">
          <Tab
            aria-label={t('support.tabs.template')}
            minW="56px"
            justifyContent="center"
          >
            <Tooltip label={t('support.tabs.template')} hasArrow>
              <Box display="flex" alignItems="center">
                <Icon as={FiFileText} boxSize={6} />
              </Box>
            </Tooltip>
          </Tab>
          <Tab
            aria-label={t('support.tabs.receives')}
            minW="56px"
            justifyContent="center"
          >
            <Tooltip label={t('support.tabs.receives')} hasArrow>
              <Box display="flex" alignItems="center">
                <Icon as={FiInbox} boxSize={6} />
              </Box>
            </Tooltip>
          </Tab>
        </TabList>

        <TabPanels>
          <TabPanel px={0} pt={3}>
            <SupportTemplateTab
              t={t}
              cardBg={cardBg}
              subtleBorder={subtleBorder}
              mutedBg={mutedBg}
              subtopicBg={subtopicBg}
              templateDirty={templateDirty}
              isFetchingTemplate={isFetchingTemplate}
              isSavingTemplate={updateTemplate.isPending}
              isTemplateLoading={isTemplateLoading}
              templateError={Boolean(templateError)}
              templateSlug={templateSlug}
              templateName={templateName}
              templateVersion={templateVersion}
              templateDefaults={templateDefaults}
              templateCategories={templateCategories}
              templateUpdatedAt={template?.updatedAt ?? null}
              onRefreshTemplate={handleTemplateRefresh}
              onSaveTemplate={handleTemplateSave}
              onTemplateSlugChange={value => {
                setTemplateSlug(value)
                markTemplateDirty()
              }}
              onTemplateNameChange={value => {
                setTemplateName(value)
                markTemplateDirty()
              }}
              onTemplateVersionChange={value => {
                setTemplateVersion(value)
                markTemplateDirty()
              }}
              onTemplateDefaultsChange={next => {
                setTemplateDefaults(next)
                markTemplateDirty()
              }}
              updateCategory={updateCategory}
              updateSubcategory={updateSubcategory}
              updateSubtopic={updateSubtopic}
              addCategory={addCategory}
              removeCategory={removeCategory}
              addSubcategory={addSubcategory}
              removeSubcategory={removeSubcategory}
              addSubtopic={addSubtopic}
              removeSubtopic={removeSubtopic}
              markTemplateDirty={markTemplateDirty}
            />
          </TabPanel>

          <TabPanel px={0} pt={6}>
            <SupportReceivesTab
              t={t}
              receives={receives}
              isReceivesLoading={isReceivesLoading}
              receivesError={receivesError as Error | null}
              filters={filters}
              onFiltersChange={next => {
                setFilters(next)
                setPage(1)
              }}
              pagination={pagination}
              onPageChange={setPage}
              onPageSizeChange={nextSize => {
                setPageSize(nextSize)
                setPage(1)
              }}
              onEditReceive={handleEditReceive}
              onRefresh={refetchReceives}
              isRefreshing={isFetchingReceives}
            />
          </TabPanel>
        </TabPanels>
      </Tabs>

      <SupportReceiveModal
        t={t}
        isOpen={isOpen}
        onClose={handleModalClose}
        selectedReceive={selectedReceive}
        editStatus={editStatus}
        editNotes={editNotes}
        payloadPreview={payloadPreview}
        supportStatuses={SUPPORT_STATUSES}
        onStatusChange={setEditStatus}
        onNotesChange={setEditNotes}
        onSave={handleReceiveSave}
        onRefresh={handleRefreshSelectedReceive}
        isSaving={updateReceive.isPending}
        isRefreshing={isFetchingReceives}
      />
    </Box>
  )
}
